/**
 * A MOLDURA DA PÁGINA — o topo que toda tela do app tem, num lugar só.
 *
 * ⚠️ NÃO CONFUNDIR com `estilos/_moldura-app.scss`, que é a moldura do
 * APLICATIVO (faixa do cabeçalho, faixa do rodapé, barra de rolagem). Esta aqui
 * é a moldura de UMA PÁGINA: navegação, título, descrição e ações.
 *
 * POR QUE EXISTE (medido em 31/08/2026, sobre as 52 páginas do app):
 *
 *   - 37 páginas abriam com `className="container py-3"` COPIADO À MÃO, e as
 *     outras variavam entre `py-4`, `py-5` e `container-fluid` sem regra.
 *   - 42 telas escreviam o título como `<h6 className="mb-0">` e 38 como
 *     `<h5 className="mb-0">`. Ninguém decidiu isso; foi a cópia que decidiu.
 *   - O "Voltar" mentia: 19 telas tinham o botão, só 4 usavam `navigate(-1)`, e
 *     o único caminho de volta inteligente era um `voltarPara` que SÓ a Agenda
 *     preenchia. Abrir a OS pela Inbox e voltar caía na lista de OS.
 *   - A Tela Cheia estava escrita à mão dentro da Agenda.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * O DESENHO — QUATRO LINHAS, UMA BORDA ESQUERDA, UM RESPIRO NO FIM.
 *
 *   ┌────────────────────────────────────────────────────────────┐
 *   │ ‹ Voltar                                                   │ 1. voltar
 *   │ Nova despesa                    [busca] [ações] [cheia][?] │ 2. título
 *   │ Lançamento do financeiro                                   │ 3. descrição
 *   │ 🏠 Início › Despesas › Nova despesa                        │ 4. breadcrumb
 *   ├────────────────────────────────────────────────────────────┤
 *   │                                                            │ ← respiro
 *   │ [ o conteúdo da tela ]                                     │
 *   └────────────────────────────────────────────────────────────┘
 *     ↑
 *     as quatro linhas começam AQUI, no mesmo pixel.
 *
 * ⛔ O VOLTAR E O BREADCRUMB CONVIVEM. SEMPRE OS DOIS.
 *
 * Eu decidi sozinho que um substituía o outro e tirei o breadcrumb das telas
 * com trilha. Errado, e ele cobriu em 31/08/2026: *"eu quero breadcrumb E quero
 * um caminho de volta para voltar a página; você fez o oposto, usou chevron
 * para fazer um breadcrumb"*. São coisas diferentes:
 *
 *   - **O VOLTAR** desfaz o último passo. Leva à tela ANTERIOR, a de verdade —
 *     estando em **Viagens** e indo lançar despesa, ele volta para **Viagens**,
 *     nunca para a raiz do módulo. O rótulo é a palavra "Voltar"; o nome do
 *     destino fica no `title`, para quem quiser conferir antes de clicar.
 *   - **O BREADCRUMB** é a árvore, e serve para SUBIR: de `Início › Despesas ›
 *     Nova despesa` salta-se direto para Despesas, sem desfazer passo a passo.
 *
 * ⚠️ E O VOLTAR SAI DA TRILHA, nunca de um destino cravado na tela. Cravar foi o
 * defeito exato que ele pegou: estando em Viagens, o botão dizia "Despesas".
 * Quem crava destino é a exceção (`voltar`), e ela existe para fluxo em etapas —
 * não para "onde essa tela costuma voltar".
 *
 * ⚠️ QUEM ALIMENTA A TRILHA É O LAYOUT, não esta moldura. Enquanto a migração
 * das 52 telas não termina, sair de uma tela ainda não migrada e cair numa
 * migrada deixava o Voltar sem nada a dizer. O `InternalLayout` registra TODA
 * navegação, com o nome que o menu já dá à tela (`nomeDaTela`); a moldura
 * registra de novo, depois, com o título específico ("OS 123" no lugar de
 * "Ordens de Serviço").
 *
 * A LINHA 1 NÃO EMPURRA NADA. Ela é um elemento solto, irmão da linha do
 * título, e não uma coluna dentro dela. Três tentativas anteriores punham a
 * seta numa coluna ao lado do título; ela empurrava o título para a direita e a
 * descrição e o breadcrumb ficavam para trás — *"nada está batendo"*.
 *
 * O BLOCO TERMINA COM UM RESPIRO, e o breadcrumb fica COLADO na descrição: as
 * quatro linhas têm de se ler como um cabeçalho só. Ele descreveu o defeito
 * antes de eu ver: *"não fica claro de que grupo o breadcrumb faz parte"*.
 * ═══════════════════════════════════════════════════════════════════════════
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { degrauAnterior, registrarNaTrilha, voltarNaTrilha } from '@/lib/trilha';
import { CustomCampoIntervalo } from '@/components/CustomCamposData';

/** O respiro de cima e de baixo. Lista FECHADA de propósito: são os passos da
 *  escala do Bootstrap que o app já usa (ver `docs/tema/12-espacamento.md`), e
 *  `padrao` é literalmente o `py-3` que 37 telas escreviam à mão. */
const RESPIRO = {
  apertado: 'py-2',
  padrao: 'py-3',
  largo: 'py-4',
} as const;

/** A largura útil. `padrao` centraliza e limita; `cheia` ocupa o monitor —
 *  serve a calendário, tabela larga e mapa, não a formulário. */
const LARGURA = {
  padrao: 'container',
  cheia: 'container-fluid',
  /** Coluna estreita, para conteúdo que se LÊ em vez de se varrer: lista de
   *  serviços, artigo, texto corrido. Linha longa cansa — a régua tipográfica
   *  é 60 a 80 caracteres, e 720px é onde isso cai neste corpo de fonte.
   *
   *  Entrou como propriedade porque apareceu o SEGUNDO uso (as duas telas de
   *  Instalações da semana, que alternavam entre lista e calendário com um
   *  `style` cravado no `<div>`). Gatilho da lei visual: o segundo uso, nunca
   *  a beleza. */
  leitura: 'container casaa-moldura-leitura',
} as const;

/** Um degrau do breadcrumb: um ancestral clicável na ÁRVORE do app. */
export type EloDoCaminho = {
  rotulo: string;
  para: string;
};

export type CustomMolduraPaginaProps = {
  /** O nome da tela. Vira o `<h5>`, o último elo do breadcrumb e o rótulo que
   *  esta tela terá no Voltar de quem vier depois dela. */
  titulo: string;
  /**
   * A LINHA ACIMA DO TÍTULO — de que coisa maior esta tela faz parte.
   *
   * Nasceu em 11/09/2026, da página da etapa: *"a gente tem que saber onde a
   * gente está; se eu estou dentro de uma etapa ou dentro de uma autorização,
   * isso ainda não está bem claro"*. O breadcrumb já dizia, mas em letra miúda,
   * disputando espaço com `Início › Autorizações ›` — e quem chega não lê
   * breadcrumb, lê o título.
   *
   * Use só quando a tela é PARTE de outra coisa nomeada: uma etapa dentro de
   * uma autorização, um ambiente dentro de uma OS. Tela de primeiro nível não
   * tem o que dizer aqui.
   *
   * ⚠️ Ela NÃO substitui o breadcrumb — decisão dele no mesmo dia. A trilha
   * continua, e esta linha é o contexto imediato em tamanho de leitura.
   */
  sobrescrito?: React.ReactNode;
  /**
   * O título COMO SE VÊ, quando ele precisa de partes com pesos diferentes.
   *
   * `titulo` continua sendo a string — é dela que saem o breadcrumb e o rótulo
   * do Voltar, e os dois querem texto puro. Esta prop troca só o que o `<h5>`
   * desenha. Dele, 11/09/2026, sobre a página da etapa: *"coloca no tamanho
   * normal 'Etapa 1.0', e depois 'Fazer contato com fornecedor'"* — o número
   * identifica, o título é o que se lê.
   */
  tituloExibido?: React.ReactNode;
  /** A linha em cinza embaixo do título. Diz o que a tela faz, não o que ela é
   *  — "Instalações e visitas das duas lojas", não "Tela de agenda". */
  descricao?: string;
  /** Os ANCESTRAIS na árvore do app, sem o `Início` (que entra sozinho) e sem a
   *  própria tela (que entra sozinha, no fim, sem link).
   *
   *  ⚠️ É a árvore, NÃO o caminho andado. A OS 123 é sempre
   *  `Início › Ordens de Serviço › OS 123`, mesmo aberta pela Inbox. */
  caminho?: EloDoCaminho[];
  /** O último degrau do breadcrumb, quando o `titulo` é longo demais para ele.
   *
   * ⚠️ Dele, 12/09/2026, olhando a tela da etapa: *"você vê que a gente já tem
   * esse mesmo texto na segunda linha da página. Talvez aí a gente precise
   * colocar só o 'Etapa 1'"*. O breadcrumb diz ONDE se está, e repetir a frase
   * inteira que já está dois dedos acima gasta a linha sem informar nada.
   *
   * Some quando não vem: a maioria das telas tem título curto, e aí o próprio
   * título é o degrau — que é o certo, porque são a mesma coisa. */
  rotuloNoCaminho?: React.ReactNode;
  /** Some com o breadcrumb mesmo sem trilha. Para telas de primeiro nível, onde
   *  ele diria só `Início › Agenda` e não informaria nada. */
  semCaminho?: boolean;
  /** A busca DESTA tela. Não confundir com a busca global, do header do app.
   *
   *  ⛔ NÃO É UM ESPAÇO LIVRE — É EMBUTIDA. A moldura desenha o campo; a tela só
   *  diz o que fazer com o que for digitado. Medido em 31/08/2026: existiam
   *  TRÊS buscas diferentes no app e nenhuma decisão por trás — a da OS e a de
   *  Custos com a lupa à esquerda e `input-group-sm`, a da Inbox com a lupa à
   *  direita e tamanho normal. Ninguém escolheu; a cópia escolheu. */
  busca?: {
    valor: string;
    aoMudar: (valor: string) => void;
    placeholder?: string;
  };
  /** O filtro de período da tela. Também EMBUTIDO, e pelo mesmo motivo.
   *
   *  A moldura não sabe o que "período" significa nesta tela — ela desenha o
   *  campo (o `CustomCampoIntervalo`, que já vem com a Data Rápida) e devolve as
   *  duas datas em ISO. Filtrar o quê, com elas, é da tela. */
  periodo?: {
    inicio: string;
    fim: string;
    aoMudar: (inicio: string, fim: string) => void;
  };
  /** Os botões próprios da tela — "Nova OS", "Exportar". Entram antes da tela
   *  cheia e da ajuda. */
  acoes?: React.ReactNode;
  /** Liga o botão de Tela Cheia. A moldura é dona do elemento que ocupa o
   *  monitor, por isso o comportamento mora aqui e não num componente solto. */
  telaCheia?: boolean;
  /** O `slug` do artigo da Central de Ajuda desta tela. Liga a interrogação. */
  ajuda?: string;
  /** Esconde o Voltar mesmo havendo trilha. Para tela que é fim de caminho e
   *  não deve convidar a sair — um formulário com dados não salvos. */
  semVoltar?: boolean;
  /** Força o destino do Voltar, ignorando a trilha.
   *
   *  ⚠️ USE COM PARCIMÔNIA — é o desenho velho, o `voltarPara` que só a Agenda
   *  preenchia e que fazia o botão mentir para quem vinha da Inbox. A trilha é a
   *  regra. Isto existe para quando o destino é uma DECISÃO da tela e não o
   *  caminho andado: o formulário de despesa manda para as próprias despesas ou
   *  para o Início conforme a permissão e o aparelho. */
  voltar?: { titulo: string; caminho: string } | null;
  respiro?: keyof typeof RESPIRO;
  largura?: keyof typeof LARGURA;
  children?: React.ReactNode;
};

export function CustomMolduraPagina({
  titulo,
  sobrescrito,
  tituloExibido,
  descricao,
  caminho = [],
  rotuloNoCaminho,
  semCaminho = false,
  busca,
  periodo,
  acoes,
  telaCheia = false,
  ajuda,
  semVoltar = false,
  voltar,
  respiro = 'padrao',
  largura = 'padrao',
  children,
}: CustomMolduraPaginaProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const areaRef = useRef<HTMLDivElement>(null);
  const [cheia, setCheia] = useState(false);

  /* O DEGRAU ANTERIOR SE LÊ ANTES DE REGISTRAR ESTA TELA. Se lêssemos depois, a
     tela atual já estaria na pilha e o Voltar apontaria para ela mesma. Depende
     só do caminho: o título muda quando a OS carrega, e recalcular por causa
     disso faria o botão piscar. */
  const daTrilha = useMemo(
    () => (semVoltar ? null : degrauAnterior(location.pathname)),
    [location.pathname, semVoltar],
  );
  /* O destino forçado vence a trilha; `semVoltar` vence os dois. */
  const anterior = semVoltar ? null : (voltar ?? daTrilha);

  /* ESTA TELA ENTRA NA TRILHA. Toda página que usa a moldura entra de graça —
     é isto que faz o Voltar da PRÓXIMA tela saber dizer "‹ Agenda". */
  useEffect(() => {
    registrarNaTrilha({ caminho: location.pathname, titulo });
  }, [location.pathname, titulo]);

  /* SAIR DA TELA CHEIA PELO ESC não passa pelo nosso botão — o navegador só
     avisa por este evento. Sem ele o ícone fica mentindo depois de a pessoa já
     ter saído. */
  useEffect(() => {
    const aoMudar = () => setCheia(document.fullscreenElement === areaRef.current);
    document.addEventListener('fullscreenchange', aoMudar);
    return () => document.removeEventListener('fullscreenchange', aoMudar);
  }, []);

  const alternarTelaCheia = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      /* `catch` vazio de propósito: o navegador recusa quando o pedido não veio
         de um gesto do usuário, e isso não é erro que valha uma tela vermelha. */
      areaRef.current?.requestFullscreen?.().catch(() => {});
    }
  };

  const aoVoltar = () => {
    if (voltar) {
      navigate(voltar.caminho);
      return;
    }
    const destino = voltarNaTrilha(location.pathname);
    if (destino) navigate(destino);
    else navigate(-1);
  };

  /* ⛔ O VOLTAR E O BREADCRUMB CONVIVEM. SEMPRE OS DOIS.
     Eu tinha decidido sozinho que um substituía o outro e tirei o breadcrumb
     das telas com trilha. Errado — ver o cabeçalho do arquivo. */
  const mostrarVoltar = Boolean(anterior);
  const mostrarCaminho = !semCaminho;

  return (
    <div ref={areaRef} className={`${LARGURA[largura]} ${RESPIRO[respiro]} casaa-moldura-pagina`}>
      {/* AS QUATRO LINHAS SÃO UM BLOCO, e é ele que carrega o respiro até o
          conteúdo. Antes o espaço vinha de `:last-of-type` nas linhas soltas —
          e `:last-of-type` conta por TIPO de elemento, não por posição: o
          `<div>` do título casava com a regra mesmo havendo cartões depois
          dele, e o breadcrumb herdava 24px em cima, exatamente o afastamento
          que ele estranhou. Um invólucro acaba com o jogo de seletor. */}
      <div className="casaa-moldura-cabecalho">
        {/* ───────────── 1. o Voltar: desfaz o último passo, sai da trilha ── */}
      {mostrarVoltar && (
        <button
          type="button"
          className="btn btn-link btn-sm casaa-moldura-voltar"
          onClick={aoVoltar}
          title={`Voltar para ${anterior!.titulo}`}
        >
          <i className="bi bi-chevron-left" />
          {/* O RÓTULO É "Voltar", não o nome da tela anterior.
              Decisão dele em 31/08/2026, depois de ver as duas: *"ao invés da
              gente colocar a página em que a pessoa estava, talvez fosse melhor
              colocar Voltar"*. O nome da tela continua no `title` — quem
              precisar saber para onde vai, passa o mouse. */}
          <span>Voltar</span>
        </button>
      )}

      {/* ───────────────────────── 2. título, busca e ações · 3. descrição ── */}
      <div className="row align-items-center page-title gx-3">
        <div className="col-12 col-sm mb-2 mb-sm-0">
          {sobrescrito && (
            <p className="casaa-moldura-sobrescrito small mb-0">{sobrescrito}</p>
          )}
          <h5 className="mb-0">{tituloExibido ?? titulo}</h5>
          {descricao && <p className="text-secondary small mb-0">{descricao}</p>}
        </div>

        {/* A BUSCA. Tamanho normal, não `input-group-sm`: a versão pequena
            aperta o placeholder, que é onde a tela ensina o que dá para buscar.
            A lupa fica à ESQUERDA — ali ela é rótulo, lido antes de digitar; à
            direita vira botão e sugere que é preciso clicar para buscar, o que
            não é verdade. */}
        {busca && (
          <div className="col-12 col-sm-auto mb-2 mb-sm-0 casaa-moldura-busca">
            <div className="input-group">
              <span className="input-group-text">
                <i className="bi bi-search" />
              </span>
              <input
                type="search"
                className="form-control"
                placeholder={busca.placeholder ?? 'Buscar…'}
                aria-label={`Buscar em ${titulo}`}
                value={busca.valor}
                onChange={(e) => busca.aoMudar(e.target.value)}
              />
            </div>
          </div>
        )}

        {/* O PERÍODO. É o `CustomCampoIntervalo`, o mesmo das telas — com a Data
            Rápida de fábrica. A moldura devolve as duas datas em ISO; filtrar o
            quê com elas é da tela. */}
        {periodo && (
          <div className="col-12 col-sm-auto mb-2 mb-sm-0 casaa-moldura-periodo">
            <CustomCampoIntervalo
              inicio={periodo.inicio}
              fim={periodo.fim}
              onChange={periodo.aoMudar}
              label="Período"
            />
          </div>
        )}

        {(acoes || telaCheia || ajuda) && (
          <div className="col-12 col-sm-auto d-flex align-items-center gap-2 justify-content-start justify-content-sm-end casaa-moldura-acoes">
            {acoes}

            {/* A TELA CHEIA SOME NO CELULAR (`d-none d-md-inline-flex`).
                No telefone o navegador já ocupa a tela toda e não há barra de
                menu para esconder — o botão não faria nada de útil e roubaria
                espaço da linha onde ele mais falta. Decisão dele, 31/08/2026.

                Do tablet para cima ele leva RÓTULO, e não só o ícone: um
                quadradinho sozinho obriga a passar o mouse para descobrir o que
                é, e esta é uma ação que se usa uma vez e some da memória. */}
            {telaCheia && (
              <button
                type="button"
                className="btn btn-outline-theme btn-sm d-none d-md-inline-flex align-items-center gap-1"
                onClick={alternarTelaCheia}
                title={cheia ? 'Sair da tela cheia' : 'Tela cheia'}
              >
                <i className={`bi bi-${cheia ? 'fullscreen-exit' : 'arrows-fullscreen'}`} />
                <span>{cheia ? 'Sair da tela cheia' : 'Tela cheia'}</span>
              </button>
            )}

            {/* A AJUDA É SEMPRE A ÚLTIMA À DIREITA — mesmo lugar em toda tela,
                senão deixa de ser reflexo e vira caça ao ícone. O ícone é o do
                tema (`life-preserver`, nas 6 páginas de exemplo do AdminUX). */}
            {ajuda && (
              <Link
                to={`/ajuda/${ajuda}`}
                className="btn btn-link btn-square"
                title="Ajuda desta tela"
                aria-label="Ajuda desta tela"
              >
                <i className="bi bi-life-preserver" />
              </Link>
            )}
          </div>
        )}
      </div>

      {/* ───────────── 4. o breadcrumb: a árvore, para SUBIR direto de nível ──
          Fica DEPOIS do título, e não antes: quem lê a tela lê primeiro onde
          está ("Nova despesa"), e só depois de onde isso pende. E fica separado
          do conteúdo pelo respiro do bloco (`_moldura-pagina.scss`) — antes ele
          encostava no primeiro cartão da tela. */}
      {mostrarCaminho && (
        <nav aria-label="breadcrumb" className="breadcrumb-theme casaa-moldura-trilha">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item bi">
              <Link to="/inicio">
                <i className="bi bi-house-door me-2" />
                Início
              </Link>
            </li>
            {caminho.map((elo) => (
              <li className="breadcrumb-item bi" key={elo.para}>
                <Link to={elo.para}>{elo.rotulo}</Link>
              </li>
            ))}
            <li className="breadcrumb-item bi active" aria-current="page">
              {rotuloNoCaminho ?? titulo}
            </li>
          </ol>
        </nav>
        )}
      </div>

      {children}
    </div>
  );
}

export default CustomMolduraPagina;
